import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => useContext(AuthContext)

// Intenta extraer el rol del JWT (funciona si el JWT hook está activo en Supabase)
const getRoleFromSession = (session) => {
  if (!session?.access_token) return null
  try {
    const payload = JSON.parse(
      atob(session.access_token.split('.')[1])
    )
    return payload?.user_role || null
  } catch {
    return null
  }
}

export const AuthProvider = ({ children }) => {
  // AuthProvider se monta DENTRO de <QueryClientProvider> (ver src/App.js),
  // por lo que useQueryClient() es válido aquí y obtenemos el cliente global
  // de React Query para poder invalidar queries cuando cambie la sesión.
  const queryClient = useQueryClient()
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const fetchingRef = useRef(false)

  const fetchProfile = async (userId, session = null) => {
    if (!userId) {
      setProfile(null)
      setUserRole(null)
      return
    }
    if (fetchingRef.current) return
    fetchingRef.current = true
    try {
      // Estrategia 1: intentar obtener rol del JWT (más rápido)
      let role = getRoleFromSession(session)

      // Obtener perfil completo de la tabla profiles
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nombre, apellido, email, rol, telefono')
        .eq('id', userId)
        .single()

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Profile error:', error.message)
        }
      }

      if (data) {
        setProfile(data)
        // Estrategia 2: rol de profiles como fallback si JWT no lo tiene
        role = role || data.rol || 'estudiante'
      } else if (session?.user?.email && session?.user?.email_confirmed_at) {
        // Fallback: crear perfil básico solo si el email ya fue confirmado.
        // El trigger de DB es el mecanismo principal; esto es un rescate
        // para usuarios que se registraron antes del trigger.
        const { error: upsertError } = await supabase.from('profiles').upsert({
          id: userId,
          email: session.user.email,
          nombre: session.user.user_metadata?.nombre
            || session.user.email.split('@')[0],
          rol: 'estudiante',
          activo: true,
        }, { onConflict: 'id' })
        if (upsertError) console.error('[AuthContext] Error upserting profile:', upsertError.message)
        role = role || 'estudiante'
      }

      // Establecer rol (JWT > profiles > default)
      setUserRole(role || 'estudiante')
    } catch (err) {
      // Ignorar errores de lock por múltiples pestañas
      if (err?.name === 'AbortError' || err?.message?.includes('lock')) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Auth lock conflict - ignorando:', err.message)
        }
        return
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('fetchProfile error:', err)
      }
    } finally {
      fetchingRef.current = false
    }
  }

  useEffect(() => {
    // Usar SOLO onAuthStateChange — el evento INITIAL_SESSION entrega
    // la sesión sin necesidad de llamar getSession() aparte, evitando
    // el conflicto de lock concurrente con navigatorLock (steal: true).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      // IMPORTANTE: callback SÍNCRONO (no async). GoTrue invoca este callback
      // DENTRO de su lock de auth. Si hacemos aquí una llamada a Supabase con
      // await (p. ej. fetchProfile, que consulta 'profiles'), esa query necesita
      // el mismo lock que el callback retiene → DEADLOCK: al volver de una
      // pestaña en background TODAS las queries quedan colgadas en la fase de
      // auth. Por eso diferimos el trabajo con setTimeout(0): el callback retorna
      // de inmediato (el lock se libera) y fetchProfile corre FUERA del lock.
      (event, session) => {
        setUser(session?.user ?? null)

        if (session?.user) {
          setTimeout(async () => {
            await fetchProfile(session.user.id, session)
            setLoading(false)
          }, 0)
        } else {
          setProfile(null)
          setUserRole(null)
          setLoading(false)
        }

        // Al refrescar el JWT, invalidar queries (diferido, fuera del lock).
        if (event === 'TOKEN_REFRESHED') {
          setTimeout(() => queryClient.invalidateQueries(), 0)
        }
      }
    )

    return () => subscription.unsubscribe()
    // queryClient es estable (instancia singleton del provider); lo omitimos
    // de las deps para mantener la suscripción montada una sola vez.
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresco de sesión POR TIEMPO, totalmente independiente de la visibilidad
  // del tab. Como autoRefreshToken está en false, GoTrue ya NO refresca al
  // cambiar de pestaña (eso era lo que trababa el cliente al volver del
  // background). Acá chequeamos cada minuto y refrescamos solo si el token está
  // por vencer (< 5 min), sin importar si el tab está activo o en background.
  // Resultado: la sesión "solo expira por tiempo" y cambiar de pestaña no
  // dispara ninguna operación de auth → no se cuelga.
  // NO interfiere con PASSWORD_RECOVERY (ese flujo vive en ResetPassword.js).
  useEffect(() => {
    const REFRESH_MARGIN_MS = 5 * 60 * 1000 // refrescar 5 min antes de expirar
    const id = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.expires_at) return
        const msToExpiry = session.expires_at * 1000 - Date.now()
        if (msToExpiry < REFRESH_MARGIN_MS) {
          await supabase.auth.refreshSession()
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Auth] refresh por tiempo:', err)
        }
      }
    }, 60 * 1000)
    return () => clearInterval(id)
  }, [])

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  }

  const registro = async (email, password, nombre, apellido, telefono) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { nombre, apellido, telefono }
      }
    })
    return { data, error }
  }

  const logout = async () => {
    // Limpiar estado React ANTES del signOut para UI inmediata
    setUser(null)
    setProfile(null)
    setUserRole(null)
    // Limpiar localStorage como safety net
    window.localStorage.removeItem('ae-auth-token')
    try {
      await supabase.auth.signOut()
    } catch {
      // signOut puede fallar si ya no hay sesión — ignorar
    }
  }

  const value = {
    user,
    profile,
    userRole,
    loading,
    login,
    registro,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
